// Models
import {
  FormFieldSchema,
  FormFieldArraySchema,
  FormFieldCustomSchema,
} from 'app/shared/models/form';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

// Utils
import { required } from 'app/shared/utils/validation';

// Constants
import { CURRENCY_CODE_OPTIONS } from 'app/modules/goAML/schemas/catalogues/currencyCodes';
import { ACCOUNT_TYPE_OPTIONS } from 'app/modules/goAML/schemas/catalogues/accountType';
import { ACCOUNT_STATUS_OPTIONS } from 'app/modules/goAML/schemas/catalogues/accountStatus';
import {
  SIGNATORY_SCHEMA,
  SIGNATORY_MY_CLIENT_SCHEMA,
} from 'app/modules/goAML/schemas/signatorySchema';
import {
  T_ENTITY_MY_CLIENT_SCHEMA,
  T_ENTITY_SCHEMA,
} from 'app/modules/goAML/schemas/tEntitySchema';

// ACCOUNT
export const ACCOUNT_INSTITUTION_NAME: FormFieldSchema<string> = {
  label: 'Institution name',
  name: 'institution_name',
  type: U21FormFieldTypes.TEXT,
  required: true,
  validate: required('Institution is required.'),
};

const SWIFT_OR_INSTITUTION_CODE: FormFieldCustomSchema<string> = {
  fieldProps: {
    fields: [
      {
        label: 'Institution Code',
        name: 'institution_code',
        type: U21FormFieldTypes.TEXT,
        validate: required('Institution code is required.'),
      },
      {
        label: 'SWIFT',
        name: 'swift',
        type: U21FormFieldTypes.TEXT,
        validate: required('SWIFT is required.'),
      },
    ],
  },
  label: 'SWIFT or Institution Code',
  // fake field needed for T_ACCOUNT_RAW_INPUTS which is iterated over for render
  name: 'swift_institution_code',
  required: true,
  type: 'CUSTOM',
};

export const ACCOUNT_NON_BANK_INSTITUTION: FormFieldSchema<string> = {
  label: 'Is banking institution?',
  name: 'non_bank_institution',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: [
      {
        value: '0',
        text: 'Yes',
      },
      {
        value: '1',
        text: 'No',
      },
    ],
  },
};

export const ACCOUNT_BRANCH: FormFieldSchema<string> = {
  label: 'Branch',
  name: 'branch',
  type: U21FormFieldTypes.TEXT,
};

export const ACCOUNT_NUMBER: FormFieldSchema<string> = {
  label: 'Account number',
  name: 'account',
  type: U21FormFieldTypes.TEXT,
  required: true,
  validate: required('Account number is required.'),
};

export const ACCOUNT_CURRENCY_CODE: FormFieldSchema<string> = {
  label: 'Currency code',
  name: 'currency_code',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: CURRENCY_CODE_OPTIONS,
  },
};

export const ACCOUNT_NAME: FormFieldSchema<string> = {
  label: 'Account name',
  name: 'account_name',
  type: U21FormFieldTypes.TEXT,
};

export const ACCOUNT_IBAN: FormFieldSchema<string> = {
  label: 'IBAN',
  name: 'iban',
  type: U21FormFieldTypes.TEXT,
};

export const ACCOUNT_CLIENT_NUMBER: FormFieldSchema<string> = {
  label: 'Client number',
  name: 'client_number',
  type: U21FormFieldTypes.TEXT,
};

export const ACCOUNT_PERSONAL_ACCOUNT_TYPE: FormFieldSchema<string> = {
  label: 'Account type',
  name: 'personal_account_type',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: ACCOUNT_TYPE_OPTIONS,
  },
};

// t_entity goes here
// signatory goes here

export const ACCOUNT_OPENED: FormFieldSchema<string> = {
  label: 'Opened date',
  name: 'opened',
  type: U21FormFieldTypes.DATE_PICKER,
};

export const ACCOUNT_CLOSED: FormFieldSchema<string> = {
  label: 'Closed date',
  name: 'closed',
  type: U21FormFieldTypes.DATE_PICKER,
};

export const ACCOUNT_BALANCE: FormFieldSchema<string> = {
  label: 'Balance',
  name: 'balance',
  type: U21FormFieldTypes.TEXT,
};

export const ACCOUNT_DATE_BALANCE: FormFieldSchema<string> = {
  label: 'Date balance',
  name: 'date_balance',
  type: U21FormFieldTypes.DATE_PICKER,
};

export const ACCOUNT_STATUS: FormFieldSchema<string> = {
  label: 'Account status',
  name: 'status_code',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: ACCOUNT_STATUS_OPTIONS,
  },
};

export const ACCOUNT_BENEFICIARY: FormFieldSchema<string> = {
  label: 'Beneficiary',
  name: 'beneficiary',
  type: U21FormFieldTypes.TEXT,
};

export const ACCOUNT_BENEFICIARY_COMMENT: FormFieldSchema<string> = {
  label: 'Beneficiary comment',
  name: 'beneficiary_comment',
  type: U21FormFieldTypes.TEXTAREA,
};

export const ACCOUNT_COMMENTS: FormFieldSchema<string> = {
  label: 'Account comments',
  name: 'comments',
  type: U21FormFieldTypes.TEXTAREA,
};

export const T_ACCOUNT_RAW_INPUTS = [
  ACCOUNT_INSTITUTION_NAME,
  SWIFT_OR_INSTITUTION_CODE,
  ACCOUNT_NON_BANK_INSTITUTION,
  ACCOUNT_BRANCH,
  ACCOUNT_NUMBER,
  ACCOUNT_CURRENCY_CODE,
  ACCOUNT_NAME,
  ACCOUNT_IBAN,
  ACCOUNT_CLIENT_NUMBER,
  ACCOUNT_PERSONAL_ACCOUNT_TYPE,
  ACCOUNT_OPENED,
  ACCOUNT_CLOSED,
  ACCOUNT_BALANCE,
  ACCOUNT_DATE_BALANCE,
  ACCOUNT_STATUS,
  ACCOUNT_BENEFICIARY,
  ACCOUNT_BENEFICIARY_COMMENT,
  ACCOUNT_COMMENTS,
];

export const T_ACCOUNT_MY_CLIENT_RAW_INPUTS = T_ACCOUNT_RAW_INPUTS.map(
  (input) => {
    // My Client has the same fields but some are required
    switch (input.name) {
      case 'currency_code':
        return {
          ...input,
          required: true,
          validate: required('Currency code is required.'),
        };
      case 'account_name':
        return {
          ...input,
          required: true,
          validate: required('Account name is required.'),
        };
      case 'client_number':
        return {
          ...input,
          required: true,
          validate: required('Client number is required.'),
        };
      case 'personal_account_type':
        return {
          ...input,
          required: true,
          validate: required('Account type is required.'),
        };
      case 'opened':
        return {
          ...input,
          required: true,
          validate: required('Open date is required.'),
        };
      case 'balance':
        return {
          ...input,
          required: true,
          validate: required('Balance is required.'),
        };
      case 'status_code':
        return {
          ...input,
          required: true,
          validate: required('Account status is required.'),
        };
      case 'beneficiary':
        return {
          ...input,
          required: true,
          validate: required('Beneficiary is required.'),
        };
      default:
        return input;
    }
  },
);

// Signatory Field
export const SIGNATORY_ARRAY_FIELD: FormFieldArraySchema<any> = {
  name: 'signatory',
  label: 'Signatories',
  fields: [...SIGNATORY_SCHEMA],
  type: 'ARRAY',
};

export const T_ACCOUNT_SCHEMA = [
  ...T_ACCOUNT_RAW_INPUTS,
  ...T_ENTITY_SCHEMA.map((field) => ({
    ...field,
    name: `t_entity.${field.name}`,
  })),
  SIGNATORY_ARRAY_FIELD,
];

// My Client
export const SIGNATORY_MY_CLIENT_ARRAY_FIELD: FormFieldArraySchema<any> = {
  ...SIGNATORY_ARRAY_FIELD,
  fields: [...SIGNATORY_MY_CLIENT_SCHEMA],
};

export const T_ACCOUNT_MY_CLIENT_SCHEMA = [
  ...T_ACCOUNT_MY_CLIENT_RAW_INPUTS,
  ...T_ENTITY_MY_CLIENT_SCHEMA.map((field) => ({
    ...field,
    name: `t_entity.${field.name}`,
  })),
  SIGNATORY_MY_CLIENT_ARRAY_FIELD,
];
